import { parseAmountCell, parseBlockHeader } from './blockHelpers';
import {
  getCell,
  getGridColumnCount,
  parsePastedGrid,
} from './parsePastedGrid';
import { findPastedOverlaps } from './multiTariffPaste';
import {
  ROOM_COMPOSITIONS,
  ROOM_TYPE_KEYS,
  findRoomComposition,
  isCompositionAllowed,
  type RoomComposition,
  type RoomTypeKey,
} from './roomCompositions';
import type {
  ChildBandsCount,
  ComparableTariff,
  ExcelPasteIssue,
  ExcelPasteResult,
  PastedPeriod,
} from './types';

export const ROOM_BLOCK_ROWS = 28;
export const ROOM_BLOCK_COLUMNS = 2;
export const FIRST_COMPOSITION_LINE = 9;

export interface PastedRoomTariff {
  name: string;
  periods: PastedPeriod[];
  /** "Total chambre" of compositions 1 to 20, null when the line is empty. */
  amounts: Array<number | null>;
}

export type RoomOptions = Record<RoomTypeKey, number[]>;
export type RoomTypesTicked = Record<RoomTypeKey, boolean>;

type PartitionLike = { price?: unknown } & Record<string, unknown>;

export interface RoomCategoryTariffState {
  name: string;
  periods: ComparableTariff['periods'];
  pricePartitions: Record<RoomTypeKey, PartitionLike[] | undefined>;
}

export interface RoomPasteContext {
  childBandsCount: ChildBandsCount;
  /** Current `*RoomOptions` of the category. */
  roomOptions: RoomOptions;
  /** Current `roomTypes` checkboxes of the category. */
  roomTypes: Partial<RoomTypesTicked>;
  /** Every tariff of the category, names already resolved for messages. */
  tariffs: RoomCategoryTariffState[];
  targetTariffIndex: number;
}

export interface RoomPasteOutcome {
  pasted: PastedRoomTariff;
  /** Compositions ticked after the paste: those priced in at least one tariff. */
  roomOptions: RoomOptions;
  /** Room types ticked after the paste: those keeping a composition. */
  roomTypes: RoomTypesTicked;
  /** Grid rows (1 to 20) the paste ticks, and those it unticks. */
  tickedRows: number[];
  untickedRows: number[];
}

const hasPrice = (partition?: PartitionLike) =>
  partition?.price !== undefined &&
  partition?.price !== null &&
  partition?.price !== '';

export function parseRoomTariffBlock(
  text: string,
  childBandsCount: ChildBandsCount,
): ExcelPasteResult<PastedRoomTariff> {
  return parseRoomTariffRows(parsePastedGrid(text), childBandsCount);
}

/** Same as parseRoomTariffBlock, on rows already split into cells. */
export function parseRoomTariffRows(
  rows: string[][],
  childBandsCount: ChildBandsCount,
): ExcelPasteResult<PastedRoomTariff> {
  if (rows.length !== ROOM_BLOCK_ROWS) {
    return {
      value: null,
      errors: [{ code: 'roomRowCount', params: { received: rows.length } }],
      warnings: [],
    };
  }

  const amountLines = ROOM_COMPOSITIONS.map(
    ({ row }) => FIRST_COMPOSITION_LINE + row - 1,
  );
  if (
    getGridColumnCount(rows) > ROOM_BLOCK_COLUMNS ||
    amountLines.some((line) => getCell(rows, line, 1) !== '')
  ) {
    return {
      value: null,
      errors: [{ code: 'roomColumns' }],
      warnings: [],
    };
  }

  const { name, periods, errors } = parseBlockHeader(rows);
  const amounts = ROOM_COMPOSITIONS.map((composition) => {
    const line = FIRST_COMPOSITION_LINE + composition.row - 1;
    const amount = parseAmountCell(rows, line, 0, errors);
    if (
      amount !== null &&
      !isCompositionAllowed(composition, childBandsCount)
    ) {
      errors.push({
        code: 'missingChildBand',
        params: {
          line,
          band:
            composition.older > 0 && childBandsCount < 2 ? 'older' : 'younger',
        },
      });
    }
    return amount;
  });

  if (errors.length === 0 && amounts.every((amount) => amount === null)) {
    errors.push({ code: 'noRoomAmount' });
  }

  if (errors.length > 0) return { value: null, errors, warnings: [] };
  return { value: { name, periods, amounts }, errors: [], warnings: [] };
}

/**
 * Options of the room types that are ticked. A type left unticked keeps the
 * options it had, and the page reloads them, but they are not offered and
 * must not come back with a paste.
 */
export function getTickedRoomOptions(
  roomOptions: RoomOptions,
  roomTypes: Partial<RoomTypesTicked>,
): RoomOptions {
  return Object.fromEntries(
    ROOM_TYPE_KEYS.map((roomType) => [
      roomType,
      roomTypes[roomType] ? [...(roomOptions[roomType] ?? [])] : [],
    ]),
  ) as RoomOptions;
}

/** Every composition the page can offer with this number of child bands. */
export function getAllAllowedRoomOptions(
  childBandsCount: ChildBandsCount,
): RoomOptions {
  return Object.fromEntries(
    ROOM_TYPE_KEYS.map((roomType) => [
      roomType,
      ROOM_COMPOSITIONS.filter(
        (composition) =>
          composition.roomType === roomType &&
          isCompositionAllowed(composition, childBandsCount),
      ).map(({ optionIndex }) => optionIndex),
    ]),
  ) as RoomOptions;
}

export const getTickedRoomTypes = (roomOptions: RoomOptions) =>
  Object.fromEntries(
    ROOM_TYPE_KEYS.map((roomType) => [
      roomType,
      roomOptions[roomType].length > 0,
    ]),
  ) as RoomTypesTicked;

/**
 * Compositions priced in at least one tariff, the rule of the grids' Expert
 * mode: what has no price anywhere is unticked. `priceOf` returns the price
 * of a composition in a tariff, or null.
 */
function keepPricedOptions(
  candidateOptions: RoomOptions,
  tariffCount: number,
  priceOf: (tariffIndex: number, composition: RoomComposition) => unknown,
): RoomOptions {
  return Object.fromEntries(
    ROOM_TYPE_KEYS.map((roomType) => [
      roomType,
      candidateOptions[roomType].filter((optionIndex) => {
        const composition = findRoomComposition(roomType, optionIndex);
        if (!composition) return false;
        return Array.from({ length: tariffCount }, (_, tariffIndex) =>
          priceOf(tariffIndex, composition),
        ).some((price) => price !== null && price !== undefined);
      }),
    ]),
  ) as RoomOptions;
}

/** Price of a ticked composition in an existing tariff (partitions follow options). */
const getExistingPrice = (
  tariff: Pick<RoomCategoryTariffState, 'pricePartitions'>,
  roomOptions: RoomOptions,
  composition: RoomComposition,
) => {
  const position = (roomOptions[composition.roomType] ?? []).indexOf(
    composition.optionIndex,
  );
  if (position === -1) return undefined;
  const partition = tariff.pricePartitions[composition.roomType]?.[position];
  return hasPrice(partition) ? Number(partition?.price) : undefined;
};

/**
 * A composition costing less than the same room with one person fewer
 * (e.g. a triple cheaper than the double): usually a column pasted in the
 * wrong tariff. The grids already apply the same check.
 */
function findPriceDecreases(amounts: PastedRoomTariff['amounts']) {
  const warnings: ExcelPasteIssue[] = [];
  const priced = ROOM_COMPOSITIONS.filter(
    ({ row }) => amounts[row - 1] !== null,
  );

  priced.forEach((larger) => {
    priced.forEach((smaller) => {
      const isOnePersonLess =
        smaller.adults + smaller.younger + smaller.older ===
          larger.adults + larger.younger + larger.older - 1 &&
        smaller.adults <= larger.adults &&
        smaller.younger <= larger.younger &&
        smaller.older <= larger.older;
      if (!isOnePersonLess) return;
      if (
        (amounts[larger.row - 1] as number) <
        (amounts[smaller.row - 1] as number)
      ) {
        warnings.push({
          code: 'priceDecreases',
          params: { larger: larger.row, smaller: smaller.row },
        });
      }
    });
  });

  return warnings;
}

export interface RoomPasteTarget {
  tariffIndex: number;
  pasted: PastedRoomTariff;
}

/**
 * Category-wide consequences of a valid room paste and its non-blocking
 * warnings. A composition stays ticked when it has a price in at least one
 * tariff of the category, the pasted one included; every other composition,
 * and every room type left without one, is unticked.
 */
export function computeRoomPasteOutcome(
  pasted: PastedRoomTariff,
  { targetTariffIndex, ...context }: RoomPasteContext,
): { outcome: RoomPasteOutcome; warnings: ExcelPasteIssue[] } {
  const { outcome, warnings } = computeRoomCategoryPasteOutcome(
    [{ tariffIndex: targetTariffIndex, pasted }],
    context,
  );
  return { outcome: { pasted, ...outcome }, warnings };
}

/**
 * Same rule for several tariffs pasted at once (a whole category). `tariffs`
 * already holds a blank state for every tariff the paste creates. With more
 * than one target, each warning names the pasted tariff it is about
 * (`inTariff`).
 */
export function computeRoomCategoryPasteOutcome(
  targets: RoomPasteTarget[],
  {
    childBandsCount,
    roomOptions,
    roomTypes,
    tariffs,
  }: Omit<RoomPasteContext, 'targetTariffIndex'>,
): {
  outcome: Omit<RoomPasteOutcome, 'pasted'>;
  warnings: ExcelPasteIssue[];
} {
  const pastedByIndex = new Map(
    targets.map(({ tariffIndex, pasted }) => [tariffIndex, pasted]),
  );
  const tickedOptions = getTickedRoomOptions(roomOptions, roomTypes);
  const priceOf = (tariffIndex: number, composition: RoomComposition) => {
    const pasted = pastedByIndex.get(tariffIndex);
    return pasted
      ? pasted.amounts[composition.row - 1]
      : getExistingPrice(tariffs[tariffIndex], tickedOptions, composition);
  };
  const nextRoomOptions = keepPricedOptions(
    getAllAllowedRoomOptions(childBandsCount),
    tariffs.length,
    priceOf,
  );

  const isTicked = (
    options: RoomOptions,
    { roomType, optionIndex }: RoomComposition,
  ) => options[roomType].includes(optionIndex);
  const tickedRows = ROOM_COMPOSITIONS.filter(
    (composition) =>
      isTicked(nextRoomOptions, composition) &&
      !isTicked(tickedOptions, composition),
  ).map(({ row }) => row);
  const untickedRows = ROOM_COMPOSITIONS.filter(
    (composition) =>
      !isTicked(nextRoomOptions, composition) &&
      isTicked(tickedOptions, composition),
  ).map(({ row }) => row);

  const warnings: ExcelPasteIssue[] = findPastedOverlaps(
    targets.map(({ pasted }) => pasted),
    tariffs.filter((_, index) => !pastedByIndex.has(index)),
  );

  // One warning per tariff listing its unpriced compositions (ticked because
  // another tariff prices them), so a category with empty tariffs does not
  // drown the recap in identical lines.
  tariffs.forEach((tariff, tariffIndex) => {
    const unpricedRows: number[] = [];
    ROOM_TYPE_KEYS.forEach((roomType) => {
      nextRoomOptions[roomType].forEach((optionIndex) => {
        const composition = findRoomComposition(roomType, optionIndex);
        if (!composition) return;
        const price = priceOf(tariffIndex, composition);
        if (price === null || price === undefined) {
          unpricedRows.push(composition.row);
        }
      });
    });
    if (unpricedRows.length > 0) {
      warnings.push({
        code: 'compositionWithoutPrice',
        params: {
          compositions: unpricedRows.sort((a, b) => a - b).join(','),
          tariff: pastedByIndex.get(tariffIndex)?.name ?? tariff.name,
        },
      });
    }
  });

  targets.forEach(({ pasted }) => {
    const decreases = findPriceDecreases(pasted.amounts);
    warnings.push(
      ...(targets.length > 1
        ? decreases.map((issue) => ({
            ...issue,
            params: { ...issue.params, inTariff: pasted.name },
          }))
        : decreases),
    );
  });

  return {
    outcome: {
      roomOptions: nextRoomOptions,
      roomTypes: getTickedRoomTypes(nextRoomOptions),
      tickedRows,
      untickedRows,
    },
    warnings,
  };
}

/**
 * Empty price written by a paste. Not `undefined`: react-hook-form shows the
 * loaded default value of the same path when a field is undefined, and after
 * options are inserted that default belongs to another composition. The
 * schema reads '' as missing, like an emptied input.
 */
export const EMPTY_PASTED_PRICE = '';

/**
 * Partitions of the pasted tariff once options are merged: one row per ticked
 * option, in option order, keeping the other fields of the existing row.
 */
export function buildPastedRoomPartitions<P extends PartitionLike>(
  roomType: RoomTypeKey,
  nextOptions: number[],
  alignedPartitions: P[],
  amounts: PastedRoomTariff['amounts'],
  createDefaultPartition: () => P,
): P[] {
  return nextOptions.map((optionIndex, position) => {
    const composition = findRoomComposition(roomType, optionIndex);
    const amount = composition ? amounts[composition.row - 1] : null;
    return {
      ...(alignedPartitions[position] ?? createDefaultPartition()),
      price: amount ?? EMPTY_PASTED_PRICE,
    };
  });
}

/**
 * Rows a paste added to another tariff of the category, for compositions it
 * ticked: their price is blanked out explicitly, for the same reason.
 */
export function blankNewRoomPartitions<P extends PartitionLike>(
  previousOptions: number[],
  nextOptions: number[],
  partitions: P[],
): P[] {
  return partitions.map((partition, position) =>
    !previousOptions.includes(nextOptions[position]) && !hasPrice(partition)
      ? { ...partition, price: EMPTY_PASTED_PRICE }
      : partition,
  );
}
